// src/pages/StoryPage.tsx - Updated for new JSON format
import React, { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { audio, storage } from '@/lib/utils'
import { DynamicStoryLoader } from '@/utils/dynamicStoryLoader'
import { useSessionStore } from '@/stores/sessionStore'
import type { ComplexityLevel } from '@/types'
import { useAnalytics } from '../hooks/useAnalytics'
import { useCurrentUserId } from '@/lib/auth-utils'
import { useChildAuth } from '@/contexts/ChildAuthContext'
import { Button } from "@/components/ui/button"

// NEW: Updated interfaces for new JSON format
interface NewStoryFormat {
  id: string
  title: string
  theme: string
  stories: {
    simple: StoryLevel
    regular: StoryLevel
    challenge: StoryLevel
  }
}

interface StoryLevel {
  narrative: string[]
  choice_points: ChoicePoint[]
  consequences: Consequence[]
  continuation: string[]
  reflections: Reflection[]
}

interface ChoicePoint {
  id: number
  question: string
  options: {
    letter: string
    text: string
  }[]
}

interface Consequence {
  choice_id: number
  letter: string
  text: string
}

interface Reflection {
  type: 'personal' | 'action'
  question: string
}

// Existing StoryBlock interface (keep for compatibility)
type StoryBlock = {
  type: 'narration' | 'choice' | 'consequence' | 'reflection' | 'action' | 'continuation' | 'conclusion'
  text: string
  choiceLetter?: string
  choicePointId?: number
  options?: { letter: string; text: string }[]
}

interface StoryPageProps {
  interest?: string
  storyName?: string
}

const StoryPage: React.FC<StoryPageProps> = ({ interest, storyName }) => {
  const [currentSection, setCurrentSection] = useState(0)
  const [complexityLevel, setComplexityLevel] = useState<ComplexityLevel>('full')
  const [story, setStory] = useState<NewStoryFormat | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [, setLocation] = useLocation()

  const { setStoryReflections } = useSessionStore()
  const [collectedReflections, setCollectedReflections] = useState<string[]>([])
  const [blocks, setBlocks] = useState<StoryBlock[]>([])
  const [currentChunk, setCurrentChunk] = useState(0)
  const [paragraphsPerChunk] = useState(2) // Show 2-3 paragraphs at a time
  const [calmMode, setCalmMode] = useState(false)
  const [isTTSActive, setIsTTSActive] = useState(false)
  const [ttsSpeed, setTtsSpeed] = useState(0.7)

  const userId = useCurrentUserId()
  const { childSession } = useChildAuth()
  const childId = childSession?.childId
  const { trackAnyActivity } = useAnalytics(childId || userId || undefined)
  const [storyStartTime] = useState(Date.now())

  const totalChunks = Math.ceil(blocks.length / paragraphsPerChunk)
  const [showStorySettings, setShowStorySettings] = useState(false)

  // Load story template
  useEffect(() => {
    const loadStory = async () => {
      try {
        setLoading(true)
        setError(null)

        const selectedInterests = JSON.parse(localStorage.getItem('selected-interests') || '[]')
        const currentInterest = interest || selectedInterests[0] || 'animals'
        const currentStoryName = storyName || 'beach-rescue'
        
        const loadedStory = await DynamicStoryLoader.loadStory(currentInterest, currentStoryName)
        
        if (loadedStory) {
          setStory(loadedStory as NewStoryFormat)
        } else {
          // Fallback story in new format
          setStory({
            id: 'fallback-story',
            title: 'Adventure Story',
            theme: 'general',
            stories: {
              simple: {
                narrative: ['There was an adventure.', 'It was fun.', 'The end.'],
                choice_points: [],
                consequences: [],
                continuation: [],
                reflections: []
              },
              regular: {
                narrative: ['There was an exciting adventure that everyone enjoyed.'],
                choice_points: [],
                consequences: [],
                continuation: [],
                reflections: []
              },
              challenge: {
                narrative: ['An extraordinary adventure unfolded, captivating everyone who experienced it.'],
                choice_points: [],
                consequences: [],
                continuation: [],
                reflections: []
              }
            }
          })
        }
      } catch (error) {
        console.error('Failed to load story:', error)
        setError('Failed to load the story. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    loadStory()
  }, [interest, storyName])

  // Load saved complexity
  useEffect(() => {
    const savedComplexity = storage.get('current-complexity-level', 'full') as ComplexityLevel
    setComplexityLevel(savedComplexity === 'regular' ? 'full' : savedComplexity)
  }, [])

  useEffect(() => {
    storage.set('current-complexity-level', complexityLevel)
  }, [complexityLevel])

  // Track reading progress through sections
  useEffect(() => {
    if (currentChunk > 0 && userId && totalChunks > 0) {
      const progressPercent = Math.round((currentChunk / totalChunks) * 100)
      if (progressPercent % 25 === 0) { // Track at 25%, 50%, 75%
        trackAnyActivity(
          'story_progress',
          `Reading ${story?.title || 'story'} - ${progressPercent}%`,
          0.5,
          { accuracy: progressPercent, completed: false }
        ).catch(error => console.log('Story progress analytics failed:', error))
      }
    }
  }, [currentChunk, totalChunks, userId, story?.title])

  // Track session on unmount (when user leaves page early)
  useEffect(() => {
    return () => {
      if (userId && storyStartTime && currentChunk < totalChunks - 1) {
        const minutes = Math.max(1, Math.round((Date.now() - storyStartTime) / 60000))
        const progressPercent = Math.round((currentChunk / Math.max(totalChunks, 1)) * 100)
        trackAnyActivity('story_reading', story?.title || 'Story', minutes, {
          accuracy: progressPercent,
          skills: ['reading_fluency'],
          completed: false
        }).catch(() => {}) // Silent fail on unmount
      }
    }
  }, []) // Empty deps - only run on unmount

  // Load mood-based TTS speed defaults
  useEffect(() => {
    const brainStateStr = storage.get('current-brain-state')
    if (brainStateStr) {
      try {
        const brainState = JSON.parse(brainStateStr)
        const moodDefaults: Record<string, number> = {
          'overwhelmed': 0.7,
          'tired': 0.7,
          'energetic': 1.1,
          'excited': 1.1,
          'focused': 0.85,
          'curious': 0.85
        }
        const defaultSpeed = moodDefaults[brainState.id] || 0.85
        const savedSpeed = storage.get('tts-speed', defaultSpeed) as number
        setTtsSpeed(savedSpeed)
      } catch (e) {
        setTtsSpeed(0.85)
      }
    } else {
      const savedSpeed = storage.get('tts-speed', 0.85) as number
      setTtsSpeed(savedSpeed)
    }
  }, [])

  // NEW: Parse new JSON format into StoryBlocks
  const parseNewFormatStory = (storyLevel: StoryLevel | null): { blocks: StoryBlock[], reflections: string[] } => {
    const blocks: StoryBlock[] = []
    const reflections: string[] = []

    // Safety checks
    if (!storyLevel) {
      console.error('❌ storyLevel is null or undefined')
      return { blocks: [], reflections: [] }
    }

    // Add narrative safely
    if (Array.isArray(storyLevel.narrative)) {
      storyLevel.narrative.forEach(text => {
        if (text && text.trim()) {
          blocks.push({
            type: 'narration',
            text: text.trim()
          })
        }
      })
    }

    // MERGE consequences into continuation (preserve them as content) - safely
    const allConsequences = Array.isArray(storyLevel.consequences) 
      ? storyLevel.consequences.map(c => c.text).filter(Boolean) 
      : []
    
    const continuation = Array.isArray(storyLevel.continuation) ? storyLevel.continuation : []
    const combinedContinuation = [...continuation, ...allConsequences]

    combinedContinuation.forEach(text => {
      if (text && text.trim()) {
        blocks.push({
          type: 'continuation',
          text: text.trim()
        })
      }
    })

    // ONLY collect reflections for CreatePage (don't render them here) - safely
    if (Array.isArray(storyLevel.reflections)) {
      storyLevel.reflections.forEach(reflection => {
        if (reflection && reflection.question) {
          reflections.push(reflection.question)
        }
      })
    }

    return { blocks, reflections }
  }

  const getCurrentStoryLevel = (): StoryLevel | null => {
    if (!story) return null
    
    const storyKey = complexityLevel === 'simple' ? 'simple' : 
                     complexityLevel === 'challenge' ? 'challenge' : 'regular'
    
    return story.stories[storyKey] || story.stories.regular
  }

  // Parse story when it changes
  useEffect(() => {
    const storyLevel = getCurrentStoryLevel()
    
    console.log('📖 Story loaded:', story)
    console.log('📊 Complexity level:', complexityLevel)
    console.log('📝 Story level object:', storyLevel)
      
    if (storyLevel) {
      const result = parseNewFormatStory(storyLevel)
      
      if (result.blocks.length === 0) {
        console.warn('⚠️ Story parsing failed, creating fallback block')
      result.blocks = [{
        type: 'narration',
        text: 'Story content is being prepared. Please try refreshing the page.',
      }]
      }
      
      setBlocks(result.blocks)
      setCollectedReflections(result.reflections)
      setCurrentChunk(prev => Math.min(prev, Math.ceil(result.blocks.length / paragraphsPerChunk) - 1))
    }
  }, [story, complexityLevel, paragraphsPerChunk])

  const handleSpeedChange = (speed: number) => {
    setTtsSpeed(speed)
    storage.set('tts-speed', speed)
    
    // If currently reading, stop and inform user to press read again
    if (isTTSActive) {
      audio.stop()
      setIsTTSActive(false)
    }
  }

  const handleReadAloud = async () => {
    if (isTTSActive) {
      audio.stop()
      setIsTTSActive(false)
      return
    }
  
    const currentBlocks = blocks.slice(
      currentChunk * paragraphsPerChunk, 
      (currentChunk + 1) * paragraphsPerChunk
    )
    
    const textToRead = currentBlocks.map(block => block.text).join(' ')
  
    setIsTTSActive(true)
    try {
      const savedAccent = storage.get('tts-accent', 'GB') as TtsAccent
      await audio.speak(textToRead, { accent: savedAccent, rate: ttsSpeed })
    } catch (error) {
      console.error('TTS error:', error)
    } finally {
      setIsTTSActive(false)
    }
  }

  const handleComplexityChange = (newLevel: ComplexityLevel) => {
    setComplexityLevel(newLevel)
  }

  const handleNextSection = () => {
    if (currentChunk === totalChunks - 1) {
      // Story completed - save reflections to sessionStore
      if (collectedReflections.length > 0) {
        setStoryReflections(collectedReflections)
        console.log('✅ Reflections saved to sessionStore:', collectedReflections)
      }
      
      // Track completion
      if (userId) {
        const readingTimeMinutes = Math.max(1, Math.round((Date.now() - storyStartTime) / 60000))
        trackAnyActivity(
          'story_reading',
          story?.title || 'Story Reading',
          readingTimeMinutes,
          {
            accuracy: 85, // Assume good comprehension if they finished
            skills: ['reading_fluency', 'comprehension'],
            completed: true
          }
        ).catch(error => console.log('Story completion analytics failed:', error))
      }
      
      // Existing logic
      setStoryReflections(collectedReflections)
      setLocation('/create')
    } else {
      setCurrentChunk(c => Math.min(totalChunks - 1, c + 1))
    }
  }

  const renderBlock = (block: StoryBlock, idx: number) => {
    return (
      <p 
        key={idx}
        className={`text-[#2D3748] text-xl md:text-2xl leading-relaxed ${
          calmMode 
            ? 'mb-8 leading-loose' 
            : 'mb-6'
        }`}
      >
        {block.text}
      </p>
    )
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p>Loading story...</p>
      </div>
    </div>
  )

  if (error && !story) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center text-red-600">
        <p>Error loading story</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded"
        >
          Try Again
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <h1 className="text-3xl font-bold text-gray-800 mb-4">{story?.title}</h1>
        <p className="text-gray-600 mb-2 text-sm">There's no rush. Read at your own pace.</p>
        {totalChunks > 1 && (
          <div className="flex justify-center gap-2 mb-4">
            {Array.from({ length: totalChunks }).map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index < currentChunk 
                    ? 'bg-[#10B981]' 
                    : index === currentChunk 
                      ? 'bg-[#2563EB]' 
                      : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}

        {/* Story Block */}
        <div className={`rounded-lg p-6 shadow-sm ${
            calmMode 
              ? 'bg-purple-50/30 border-2 border-purple-100' 
              : 'bg-white'
        }`}>
          
          {/* Story Controls - Top Right */}
          <div className="flex justify-end gap-2 mb-4">
            <button
                onClick={() => handleReadAloud()}
                className="p-2 rounded-lg bg-transparent border border-[#E5E7EB] hover:bg-gray-50 transition-colors min-h-[36px] min-w-[36px]"
                title={isTTSActive ? 'Stop reading' : 'Read aloud'}
              >
                <span className="text-lg">{isTTSActive ? '⏸️' : '🔊'}</span>
            </button>
            <button
                onClick={() => setShowStorySettings(!showStorySettings)}
                className="p-2 rounded-lg bg-transparent border border-[#E5E7EB] hover:bg-gray-50 transition-colors min-h-[36px] min-w-[36px]"
                title="Story settings"
              >
                <span className="text-lg">⚙️</span>
            </button>
          </div>

          {/* Minimal Speed Popover - Only shows if settings clicked */}
          {showStorySettings && (
            <div className="absolute right-0 top-12 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-10">
              <p className="text-xs text-[#6B7280] mb-2">Reading Speed:</p>
              <div className="flex gap-2">
                <button onClick={() => { handleSpeedChange(0.7); setShowStorySettings(false); }}
                  className={`px-2 py-1 rounded text-xs ${Math.abs(ttsSpeed - 0.7) < 0.01 ? 'bg-[#10B981] text-white' : 'bg-gray-100'}`}>
                  Slow
                </button>
                <button onClick={() => { handleSpeedChange(0.85); setShowStorySettings(false); }}
                  className={`px-2 py-1 rounded text-xs ${Math.abs(ttsSpeed - 0.85) < 0.01 ? 'bg-[#10B981] text-white' : 'bg-gray-100'}`}>
                  Normal
                </button>
                <button onClick={() => { handleSpeedChange(1.1); setShowStorySettings(false); }}
                  className={`px-2 py-1 rounded text-xs ${Math.abs(ttsSpeed - 1.1) < 0.01 ? 'bg-[#10B981] text-white' : 'bg-gray-100'}`}>
                  Fast
                </button>
              </div>
            </div>
          )}

          {/* Story Content */}
          {blocks.length > 0 ? (
            <>
              {blocks
                .slice(currentChunk * paragraphsPerChunk, (currentChunk + 1) * paragraphsPerChunk)
                .map((block, idx) => renderBlock(block, currentChunk * paragraphsPerChunk + idx))}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Story loading...</p>
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          )}
        </div>

        {/* Inline Complexity Switcher */}
        <div className="flex justify-center items-center gap-2 mt-6 mb-4">
          <span className="text-xs text-[#6B7280]">Difficulty:</span>
          {(['simple', 'full', 'challenge'] as const).map((level) => (
            <button 
              key={level} 
              onClick={() => handleComplexityChange(level)}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                complexityLevel === level 
                  ? 'bg-[#10B981] text-white' 
                  : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'
              }`}
            >
              {level === 'simple' ? 'Easy' : level === 'full' ? 'Just Right' : 'Challenge'}
            </button>
          ))}
        </div>

        {/* Simplified Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setLocation('/interests')}
            className="text-sm text-[#6B7280] hover:text-[#2D3748] min-h-[44px] px-3"
          >
            ← Back
          </button>
          
          <button
            onClick={handleNextSection}
            className="px-6 py-3 bg-[#10B981] hover:bg-[#059669] text-white rounded-lg min-h-[48px] font-medium shadow-sm"
          >
            {currentChunk === totalChunks - 1 ? 'Create Something →' : 'Next →'}
          </button>
        </div>

      </div>
    </div>
  )
}

export default StoryPage